import { apiClient } from './apiClient.js';
import type {
  ApiResponse,
  CcDashboardData,
  CcFacility,
  CreateCcFacilityRequest,
  UpdateCcFacilityRequest,
  CreateCcAdvocateRequest,
  UpdateCcAdvocateRequest,
  CreateCcActivityLogRequest,
  UpdateCcDealStatusRequest,
  UpsertCcNetworkRequest,
} from '@onboarding-dashboard/shared';

export const commandCenterService = {
  async getDashboardData(): Promise<ApiResponse<CcDashboardData>> {
    return apiClient.get<CcDashboardData>('/command-center/dashboard');
  },

  async createFacility(data: CreateCcFacilityRequest): Promise<ApiResponse<CcFacility>> {
    return apiClient.post<CcFacility>('/command-center/facilities', data);
  },

  async updateFacilityStage(id: string, stage: string): Promise<ApiResponse<CcFacility>> {
    return apiClient.patch<CcFacility>(`/command-center/facilities/${id}/stage`, { stage });
  },

  async updateFacilityNotes(id: string, notes: string): Promise<ApiResponse<CcFacility>> {
    return apiClient.patch<CcFacility>(`/command-center/facilities/${id}/notes`, { notes });
  },

  async updateFacility(
    id: string,
    data: UpdateCcFacilityRequest
  ): Promise<ApiResponse<CcFacility>> {
    return apiClient.patch<CcFacility>(`/command-center/facilities/${id}`, data);
  },

  async deleteFacility(id: string): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/command-center/facilities/${id}`);
  },

  async updateDealStatus(
    id: string,
    data: UpdateCcDealStatusRequest
  ): Promise<ApiResponse<CcFacility>> {
    return apiClient.patch<CcFacility>(`/command-center/facilities/${id}/deal-status`, data);
  },

  async upsertMilestone(
    facilityId: string,
    stageKey: string,
    date?: string
  ): Promise<ApiResponse<unknown>> {
    return apiClient.put(`/command-center/facilities/${facilityId}/milestones/${stageKey}`, {
      date,
    });
  },

  async updateAdvocates(
    facilityId: string,
    advocates: CreateCcAdvocateRequest[]
  ): Promise<ApiResponse<null>> {
    return apiClient.put<null>(`/command-center/facilities/${facilityId}/advocates`, { advocates });
  },

  async createAdvocate(
    facilityId: string,
    data: CreateCcAdvocateRequest
  ): Promise<ApiResponse<unknown>> {
    return apiClient.post(`/command-center/facilities/${facilityId}/advocates`, data);
  },

  async updateAdvocate(id: string, data: UpdateCcAdvocateRequest): Promise<ApiResponse<unknown>> {
    return apiClient.patch(`/command-center/advocates/${id}`, data);
  },

  async deleteAdvocate(id: string): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/command-center/advocates/${id}`);
  },

  async createActivityLog(
    facilityId: string,
    data: CreateCcActivityLogRequest
  ): Promise<ApiResponse<unknown>> {
    return apiClient.post(`/command-center/facilities/${facilityId}/activity`, data);
  },

  async upsertNetwork(data: UpsertCcNetworkRequest): Promise<ApiResponse<unknown>> {
    return apiClient.put('/command-center/networks', data);
  },

  async deleteNetwork(name: string): Promise<ApiResponse<null>> {
    return apiClient.delete<null>(`/command-center/networks/${encodeURIComponent(name)}`);
  },
};
